import axios from 'axios';
import { API_URL } from './api';

const BUFFER_PERCENTAGE = 15; // 15% buffer for rate changes

// Coerces a possibly-string decimal to a finite number, 0 otherwise.
const num = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

// Helper function to estimate package dimensions based on items
const estimatePackageDimensions = (items) => {
  // If no items, return default dimensions
  if (!items || items.length === 0) {
    return {
      length: 12,
      width: 8,
      height: 6,
      unit: "inch"
    };
  }

  // Calculate total volume and get max dimensions
  let totalVolume = 0;
  let maxLength = 0;
  let maxWidth = 0;
  let maxHeight = 0;

  items.forEach(item => {
    // Dims are decimal(12,3) and may arrive as strings — coerce before the ||
    // fallback so a "0.000" string doesn't read as a supplied dimension.
    const len = num(item.dimLen) || 12;
    const wdt = num(item.dimWdt) || 8;
    const hgt = num(item.dimHgt) || 6;
    const qty = num(item.quantity) || 1;

    totalVolume += len * wdt * hgt * qty;
    maxLength = Math.max(maxLength, len);
    maxWidth = Math.max(maxWidth, wdt);
    maxHeight = Math.max(maxHeight, hgt);
  });

  // Estimate dimensions maintaining aspect ratio
  const cubicRoot = Math.cbrt(totalVolume);
  return {
    length: Math.max(maxLength, cubicRoot),
    width: Math.max(maxWidth, cubicRoot * 0.8),
    height: Math.max(maxHeight, cubicRoot * 0.6),
    unit: "inch"
  };
};

// Helper function to estimate number of packages needed
const estimatePackageCount = (items, maxWeight = 50) => {
  if (!items || items.length === 0) return 1;
  
  const totalWeight = items.reduce((sum, item) => {
    return sum + ((num(item.weight) || 1) * (num(item.quantity) || 1));
  }, 0);

  return Math.max(1, Math.ceil(totalWeight / maxWeight));
};

export const getShippingRates = async (toAddress, items, shippingMethod, deliveryDate, fromAddress) => {
  try {
    // Validate addresses
    if (!fromAddress?.address_line1 || !fromAddress?.city_locality || 
        !fromAddress?.state_province || !fromAddress?.postal_code) {
      throw new Error('Incomplete warehouse address information');
    }

    if (!toAddress?.address_line1 || !toAddress?.city_locality || 
        !toAddress?.state_province || !toAddress?.postal_code) {
      throw new Error('Incomplete shipping address information');
    }

    // Estimate package dimensions and count
    const estimatedDimensions = estimatePackageDimensions(items);
    const estimatedPackageCount = estimatePackageCount(items);
    
    // Calculate total weight
    const totalWeight = items.reduce((sum, item) => {
      return sum + ((num(item.weight) || 1) * (num(item.quantity) || 1));
    }, 0);

    // Create packages array based on estimates
    const packages = [];
    const weightPerPackage = totalWeight / estimatedPackageCount;
    
    for (let i = 0; i < estimatedPackageCount; i++) {
      packages.push({
        weight: {
          value: weightPerPackage,
          unit: "pound"
        },
        dimensions: estimatedDimensions
      });
    }

    console.log('Sending request to ShipEngine with:', {
      fromAddress,
      toAddress,
      packages,
      shippingMethod,
      deliveryDate
    });

    const response = await axios.post(`${API_URL}/api/shipping/rates`, {
      fromAddress,
      toAddress,
      packages,
      shippingMethod,
      deliveryDate
    }, {
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.data?.rate_response?.rates) {
      console.error('Invalid response from shipping rate API:', response.data);
      throw new Error('Invalid response from shipping rate API');
    }

    // Add rate ranges and estimation info to each rate
    const ratesWithRanges = response.data.rate_response.rates.map(rate => ({
      ...rate,
      id: rate.rate_id,
      carrier: rate.carrier_friendly_name,
      service: rate.service_type,
      price: rate.shipping_amount.amount,
      estimated_range: {
        low: parseFloat(rate.shipping_amount.amount),
        high: parseFloat(rate.shipping_amount.amount) * (1 + BUFFER_PERCENTAGE / 100)
      },
      is_estimate: true,
      estimate_message: `Rate may vary based on final packaging (${estimatedPackageCount} estimated package${estimatedPackageCount > 1 ? 's' : ''})`
    }));

    return {
      ...response.data,
      rate_response: {
        ...response.data.rate_response,
        rates: ratesWithRanges
      }
    };
  } catch (error) {
    console.error('Error fetching shipping rates:', error);
    throw error;
  }
};

export const displayShippingOptions = (ratesData) => {
  if (!ratesData?.rate_response?.rates) {
    return [];
  }

  return ratesData.rate_response.rates.map(rate => ({
    id: rate.rate_id,
    carrier: rate.carrier_friendly_name,
    service: rate.service_type,
    price: rate.shipping_amount.amount,
    estimated_range: rate.estimated_range,
    is_estimate: rate.is_estimate,
    estimate_message: rate.estimate_message,
    service_code: rate.service_code,
    carrier_id: rate.carrier_id,
    rate_id: rate.rate_id,
    shipping_amount: rate.shipping_amount,
    delivery_days: rate.delivery_days
  }));
}; 